/**
 * TotalsPanel.jsx - the builder's live totals, straight from POST
 * /api/quotes/calc: the money, the hours by tier, and the workbook's red
 * warnings (travel days with no airfare - the classic Offline-task mistake).
 * Money in the rate card's currency (the calc says which).
 */
import { GREEN, LINE, MUTED, fmtMoney as fmtCur, fmtNum } from './quoteUi';

const Row = ({ label, value, strong, muted }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      gap: 10,
      padding: '4px 0',
      borderBottom: '1px solid #F1F3EE',
      fontSize: strong ? 13 : 11.5,
      fontWeight: strong ? 800 : 600,
      color: muted ? MUTED : strong ? GREEN : '#1F2328',
    }}
  >
    <span>{label}</span>
    <span style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</span>
  </div>
);

export default function TotalsPanel({ type, calc, busy, error, showRate = false, currency }) {
  const cur = currency || (calc && calc.currency);
  const fmtMoney = (v) => fmtCur(v, cur);
  return (
    <div style={{ background: '#fff', border: `1px solid ${LINE}`, borderRadius: 10, padding: '12px 14px' }} aria-live="polite">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
        <span style={{ fontSize: 11, fontWeight: 800, color: GREEN, letterSpacing: 0.6, textTransform: 'uppercase' }}>Totals</span>
        <span style={{ fontSize: 10, color: MUTED }}>{busy ? 'working out...' : ''}</span>
      </div>
      {error && (
        <div role="alert" style={{ fontSize: 11.5, color: '#B71C1C', fontWeight: 600, marginBottom: 6 }}>
          {error}
        </div>
      )}
      {!calc || (type === 'worksheet' && !calc.quoteData) ? (
        <div style={{ fontSize: 11.5, color: MUTED }}>Nothing worked out yet.</div>
      ) : type === 'worksheet' ? (
        <>
          <Row label="Labour" value={fmtMoney(calc.quoteData.laborTotal)} />
          <Row label="Billable expenses" value={fmtMoney(calc.quoteData.expensesTotal)} />
          {calc.quoteData.prevOverages > 0 && <Row label="Previous overages" value={fmtMoney(calc.quoteData.prevOverages)} />}
          <Row label="Total budget" value={fmtMoney(calc.quoteData.budget)} strong />
          {calc.seesCosts && calc.asSold && (
            <>
              <Row label="COGS (labour + all expenses)" value={fmtMoney(calc.asSold.all.cost)} muted />
              <Row label="Gross profit" value={fmtMoney(calc.asSold.all.profit)} muted />
              <Row label="Margin" value={`${(calc.asSold.all.margin * 100).toFixed(2)}%`} strong />
              <Row label="Labour-only margin" value={`${(calc.asSold.labor.margin * 100).toFixed(2)}%`} muted />
            </>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, marginTop: 8 }}>
            {[
              ['NT', calc.totals.nt],
              ['OT', calc.totals.ot],
              ['DT', calc.totals.dt],
              ['Hours', calc.totals.hours],
              ['Bill. days', calc.totals.bDays],
              ['Engineers', calc.engineers.filter((e) => e.hours > 0).length],
            ].map(([k, v]) => (
              <div key={k} style={{ background: '#F6F8F3', borderRadius: 6, padding: '4px 6px', textAlign: 'center' }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: MUTED }}>{k}</div>
                <div style={{ fontSize: 13, fontWeight: 800 }}>{fmtNum(v)}</div>
              </div>
            ))}
          </div>
        </>
      ) : type === 'parts' ? (
        <>
          <Row label="Subtotal" value={fmtMoney(calc.totals.subtotal)} />
          {calc.totals.discount > 0 && (
            <Row label={`Discount (${fmtNum(calc.totals.discountPct)}%)`} value={`-${fmtMoney(calc.totals.discount)}`} />
          )}
          <Row label="Freight / handling" value={calc.totals.freightTBD ? 'TBD' : fmtMoney(calc.totals.freight)} />
          <Row label={`Grand total${calc.totals.tbdCount ? ' *' : ''}`} value={fmtMoney(calc.totals.total)} strong />
          {calc.totals.tbdCount > 0 && <Row label="* Lines priced TBD" value={String(calc.totals.tbdCount)} muted />}
        </>
      ) : (
        <>
          <Row label={showRate ? `Labour (rate ${fmtMoney(calc.rate)}/hr)` : 'Labour'} value={fmtMoney(calc.totals.laborUsd)} />
          {calc.totals.laborFixed > 0 && <Row label="of which Fixed Fee" value={fmtMoney(calc.totals.laborFixed)} muted />}
          <Row label="Expenses" value={fmtMoney(calc.totals.expTotal)} />
          {calc.totals.feeAmount > 0 && <Row label="of which handling fee" value={fmtMoney(calc.totals.feeAmount)} muted />}
          {calc.totals.prevOverages > 0 && <Row label="Previous overages" value={fmtMoney(calc.totals.prevOverages)} />}
          <Row label={calc.totals.fco ? 'Total budget (FCO)' : 'Quote total'} value={fmtMoney(calc.totals.quoteTotal)} strong />
          {calc.totals.optionUsd > 0 && <Row label="Additional option" value={fmtMoney(calc.totals.optionUsd)} />}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, marginTop: 8 }}>
            {[
              ['NT', calc.totals.nt],
              ['OT', calc.totals.ot],
              ['DT', calc.totals.dt],
              ['Hours', calc.totals.laborHours],
              ['Days', calc.totals.daysWorked],
              ['Travel', calc.totals.travelDays],
            ].map(([k, v]) => (
              <div key={k} style={{ background: '#F6F8F3', borderRadius: 6, padding: '4px 6px', textAlign: 'center' }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: MUTED }}>{k}</div>
                <div style={{ fontSize: 13, fontWeight: 800 }}>{fmtNum(v)}</div>
              </div>
            ))}
          </div>
        </>
      )}
      {calc &&
        (calc.warnings || []).map((w) => (
          <div
            key={w}
            role="alert"
            style={{
              marginTop: 8,
              padding: '6px 8px',
              background: '#FFEBEE',
              border: '1px solid #FFCDD2',
              borderRadius: 6,
              fontSize: 11,
              color: '#B71C1C',
              fontWeight: 600,
            }}
          >
            {w}
          </div>
        ))}
      {calc &&
        (calc.notes || []).map((w) => (
          <div key={w} style={{ marginTop: 8, fontSize: 10.5, color: MUTED }}>
            {w}
          </div>
        ))}
      {calc && calc.problem && (
        <div
          style={{
            marginTop: 8,
            padding: '6px 8px',
            background: '#FFF8E1',
            border: '1px solid #FDE68A',
            borderRadius: 6,
            fontSize: 11,
            color: '#92400E',
            fontWeight: 600,
          }}
        >
          Not ready for a document yet: {calc.problem}
        </div>
      )}
    </div>
  );
}
